import { apiAddress } from './apiModel';
import { clearAuthStorage, WebShopBimRefreshToken, WebShopBimTAccessToken } from './common';

// Routes that genuinely need a session. Every other route renders fine logged out, so an
// expired session there must not navigate the user off the page they actually asked for.
const protectedRoutes = ['/profilepage', '/createuserdata', '/portal/createitems'];

export function isProtectedRoute(pathname: string): boolean {
    const route = pathname.toLowerCase();
    return protectedRoutes.some((protectedRoute) => route.startsWith(protectedRoute));
}

// Set once from the store, which owns the reset/redirect policy. Registering it here instead
// of importing the store keeps this module free of a circular dependency on it.
let onSessionExpired: () => void = () => { };

export function setSessionExpiredHandler(handler: () => void) {
    onSessionExpired = handler;
}

async function requestNewAccessToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem(WebShopBimRefreshToken);
    if (!refreshToken) {
        return false;
    }

    const response = await fetch(apiAddress + 'auth/refresh', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + refreshToken }
    });

    if (response.status !== 200) {
        return false;
    }

    const accessToken = await response.json() as string;
    if (typeof accessToken !== 'string' || accessToken === '') {
        return false;
    }

    localStorage.setItem(WebShopBimTAccessToken, accessToken);
    return true;
}

// A page mount fires half a dozen requests at once, and they all get their 401 at roughly the
// same moment. Share one refresh between them instead of spending one per request -- the
// server would invalidate the refresh token on the first call and fail the rest.
let refreshInFlight: Promise<boolean> | null = null;

export function refreshAccessTokenOnce(): Promise<boolean> {
    if (refreshInFlight === null) {
        refreshInFlight = requestNewAccessToken()
            .catch(() => false)
            .finally(() => { refreshInFlight = null; });
    }
    return refreshInFlight;
}

function withAccessToken(init: RequestInit): RequestInit {
    const headers = new Headers(init.headers);
    const accessToken = localStorage.getItem(WebShopBimTAccessToken);

    if (accessToken) {
        headers.set('Authorization', 'Bearer ' + accessToken);
    }
    else {
        headers.delete('Authorization');
    }

    return { ...init, headers };
}

/**
 * fetch for endpoints that need the centralization access token.
 *
 * Reads the token at call time, and on a 401 refreshes once and replays the request. Doing
 * this here rather than in redux middleware is the only place it can work: middleware sees
 * the thunk's pending action, but createAsyncThunk invokes the payload creator -- and so the
 * request -- immediately afterwards, so middleware can never get a fresh token in first.
 *
 * If the refresh fails the session really is over: the stored tokens are cleared and the
 * original 401 is handed back, so each slice's existing error handling still runs.
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const response = await fetch(url, withAccessToken(init));

    if (response.status !== 401) {
        return response;
    }

    const refreshed = await refreshAccessTokenOnce();
    if (!refreshed) {
        clearAuthStorage();
        onSessionExpired();
        return response;
    }

    return fetch(url, withAccessToken(init));
}
